const RESERVED_FIRST_SEGMENTS = new Set([
  "login",
  "register",
  "sales-rep-register",
  "forgot-password",
  "reset-password",
  "forgot-username",
  "contact",
  "transaction",
  "dashboard",
  "profile",
  "seller",
  "buyer",
  "propnine",
  "refinance",
  "refi",
  "seller2net",
  "open",
  "settings",
  "settings-page",
  "admin",
  "master",
  "estimated-closing",
  "demo",
  "demo-ended",
]);

export const LANDING_COMPANY_STORAGE_KEY = "landing_company_name";

export function getLandingCompanyNameFromPath(pathname = "") {
  const firstSegment = decodeURIComponent(
    String(pathname || "")
      .replace(/^\/+|\/+$/g, "")
      .split("/")[0] || "",
  ).trim();

  if (!firstSegment) return null;
  if (RESERVED_FIRST_SEGMENTS.has(firstSegment.toLowerCase())) return null;
  return firstSegment;
}

export function isHostBasedLoginPath(pathname = "") {
  const normalized = String(pathname || "/").replace(/\/+$/, "") || "/";
  return normalized === "/" || normalized === "/login";
}

// The "Try the Live Demo" / "Request a Demo" button on costsfirst.com must
// link to https://demo.costsfirst.com/?ref=costsfirst (this exact param and
// value). A URL param, not document.referrer: the referrer is silently
// stripped by some combination of the marketing site's link markup / a
// referrer-policy meta tag / response header — confirmed in production,
// where a real click-through still showed an empty document.referrer. A
// query param can't be dropped by browser referrer policy, ever.
const MARKETING_REF_PARAM = "ref";
const MARKETING_REF_VALUE = "costsfirst";

function isReferredFromMarketingSite(search = window.location.search) {
  try {
    return new URLSearchParams(search).get(MARKETING_REF_PARAM) === MARKETING_REF_VALUE;
  } catch {
    return false;
  }
}

// Local development machines (npm start, .env.local) skip the referrer
// check below — there's no marketing site to click through from when
// testing locally, and requiring one would break that workflow entirely.
function isLocalDevEnvironment() {
  const env = process.env.REACT_APP_NODE_ENV;
  return env === "local" || env === "development";
}

// The guest-demo entry point (e.g. demo.costsfirst.com) is its own hostname,
// not a path-based tenant — resolved separately from the landing-company-name
// scheme above so it never gets treated as (or collides with) a tenant path.
//
// On deployed builds (demo/staging/production), matching the hostname alone
// isn't enough: the guest flow only opens for visitors who arrived via the
// ?ref=costsfirst link on costsfirst.com, not anyone who discovers,
// bookmarks, or types the demo subdomain directly. Local dev is exempt
// (see isLocalDevEnvironment).
// Hostname-only half of isDemoHost, exported separately: some callers (e.g.
// ThemeContext's cache check) need to know "is this the demo tenant's
// domain" on every page of the guest journey, not just the initial landing
// hit — and by the time a guest reaches /dashboard, the ?ref=costsfirst
// param from the original link is long gone from the URL.
export function isDemoHostname(hostname = window.location.hostname) {
  const configuredHost = process.env.REACT_APP_DEMO_HOSTNAME;
  return configuredHost
    ? hostname === configuredHost
    : hostname.split(".")[0].toLowerCase() === "demo";
}

export function isDemoHost(hostname = window.location.hostname) {
  const hostnameMatches = isDemoHostname(hostname);

  // TEMP DEBUG — remove once the demo.costsfirst.com guest-access
  // investigation is closed. Open DevTools Console on the live site to see
  // exactly which check failed.
  // eslint-disable-next-line no-console
  console.log("[isDemoHost] checking", {
    hostname,
    configuredHost: process.env.REACT_APP_DEMO_HOSTNAME || "(none — using fallback)",
    hostnameMatches,
    "REACT_APP_NODE_ENV": process.env.REACT_APP_NODE_ENV,
    isLocalDev: isLocalDevEnvironment(),
    "window.location.search": window.location.search || "(empty)",
    isReferredFromMarketingSite: isReferredFromMarketingSite(),
  });

  if (!hostnameMatches) {
    // eslint-disable-next-line no-console
    console.log("[isDemoHost] → false: hostname did not match");
    return false;
  }
  if (isLocalDevEnvironment()) {
    // eslint-disable-next-line no-console
    console.log("[isDemoHost] → true: local dev, ?ref check skipped");
    return true;
  }
  const referred = isReferredFromMarketingSite();
  // eslint-disable-next-line no-console
  console.log(`[isDemoHost] → ${referred}: ?ref=costsfirst check result`);
  return referred;
}

export function getStoredLandingCompanyName() {
  try {
    const name = localStorage.getItem(LANDING_COMPANY_STORAGE_KEY)?.trim();
    if (!name) return null;
    // Self-heals a value that's reserved NOW but wasn't when it was written
    // (e.g. "demo-ended", persisted before it was added to
    // RESERVED_FIRST_SEGMENTS) — otherwise it silently poisons every future
    // theme/domain lookup in this browser forever, since nothing else ever
    // re-validates an already-stored value.
    if (RESERVED_FIRST_SEGMENTS.has(name.toLowerCase())) {
      localStorage.removeItem(LANDING_COMPANY_STORAGE_KEY);
      return null;
    }
    return name;
  } catch {
    return null;
  }
}

export function getActiveLandingCompanyName(pathname = window.location.pathname) {
  return (
    getLandingCompanyNameFromPath(pathname) || getStoredLandingCompanyName()
  );
}

export function persistLandingCompanyName(name) {
  if (!name) return;
  try {
    localStorage.setItem(LANDING_COMPANY_STORAGE_KEY, name);
  } catch {
    // ignore storage errors
  }
}

export function buildCompanyPath(subPath = "", companyName) {
  const name = (companyName ?? getStoredLandingCompanyName())?.trim();
  const cleanPath = String(subPath || "").replace(/^\/+/, "");

  if (!name) {
    if (!cleanPath || cleanPath === "login") return "/login";
    return `/${cleanPath}`;
  }

  const encoded = encodeURIComponent(name);
  if (!cleanPath || cleanPath === "login") {
    return `/${encoded}`;
  }
  return `/${encoded}/${cleanPath}`;
}

const AUTH_SUB_PATHS = new Set([
  "register",
  "sales-rep-register",
  "forgot-password",
  "reset-password",
  "forgot-username",
  "contact",
]);

export function getCompanyPrefixedRedirect(pathname = "", search = "") {
  if (isDemoHost()) return null;

  const stored = getStoredLandingCompanyName();
  if (!stored) return null;
  if (getLandingCompanyNameFromPath(pathname)) return null;

  const normalized = String(pathname || "/").replace(/\/+$/, "") || "/";
  const segments = normalized.split("/").filter(Boolean);

  if (
    segments.length === 0 ||
    (segments.length === 1 && segments[0].toLowerCase() === "login")
  ) {
    return `${buildCompanyPath("", stored)}${search}`;
  }

  const first = segments[0].toLowerCase();
  if (first === "register" && segments[1] === "success") {
    return `${buildCompanyPath("register/success", stored)}${search}`;
  }

  if (AUTH_SUB_PATHS.has(first)) {
    return `${buildCompanyPath(segments.join("/"), stored)}${search}`;
  }

  return null;
}

export function resolveThemeDomain(pathname = window.location.pathname) {
  const landingName = getActiveLandingCompanyName(pathname);
  if (landingName) {
    persistLandingCompanyName(landingName);
    return landingName;
  }

  if (isHostBasedLoginPath(pathname)) {
    return window.location.host;
  }

  return window.location.host;
}
